/* eslint-disable no-negated-condition */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';

import { apiConfig, Theme } from '@constants';
import { useGetTileInventory, useGetUserLocation, useLoadingManager, useMapAnimation, useMercatorMap } from '@hooks';

import { MapTileLayer } from './components/MapTileLayer';
import { MapAttribution } from './MapAttribution';
import { MapDebugView } from './MapDebugView';
import { MapHomePin, MapHomePinSize } from './MapHomePin';
import { MapLegend, MapLegendSizeClass } from './MapLegend';
import { MapLoading } from './MapLoading';
import { OverlayType, OverlayTypeInfo, ZoomPreset } from './MapTypes';
import { Overlay, OverlayTypeTitle, PromotedOverlay } from './Overlay';
import { ZoomPresetCenter, ZoomPresetZoom } from './ZoomPreset';

import sadCloud from '../../assets/sad_cloud.png';
import { isWeb } from '../../helpers/Platform';
import { MapTileSizeMethod } from '../../hooks/useMercatorMap';
import { TWCText } from '../primitives/TWCText/TWCText';

export { MapHomePinSize, MapLegend, MapLegendSizeClass, OverlayType, OverlayTypeInfo, OverlayTypeTitle, ZoomPreset };

const animationMaxFrames = 8;
// The number of frames to hold the final animation frame for before resetting to the first frame.
// Final frame will be shown for holdFrames * animationInterval milliseconds.
const holdFrames = 2;

const baseMapLoadingKey = 'baseMap';
const symbolLayerLoadingKey = 'symbolLayer';

const errorTPath = 'errors.';

const setLayerOpacity = (layerReference, opacity) => {
  if (isWeb) {
    if (layerReference?.style != null) {
      layerReference.style.opacity = opacity;
    } else if (layerReference != null) {
      layerReference.style = { opacity };
    }
  } else {
    layerReference?.setNativeProps({
      opacity,
    });
  }
};

const MapAPI = ({
  zoomPreset,
  overlayType,
  onFrameChange,
  attributionStyle,
  animate,
  debug,
  homePinSize,
  isVisibleOverride,
  useDefaultLocation,
}) => {
  // Kick off the inventory fetch.
  const { data: inventoryData, error: inventoryError, isFetching: inventoryIsFetching } = useGetTileInventory();

  // We'll fill this with parsed data from our inventory response.
  // Keys: series keys (temp, radar, etc).
  // Values: An array of animation frame objects.  Animation frame objects contain a ts and optional fts value.
  const parsedInventory = {};

  if (inventoryData) {
    const inventoryKeys = Object.keys(inventoryData.seriesInfo);

    for (const [, inventoryKey] of inventoryKeys.entries()) {
      const inventoryValue = inventoryData.seriesInfo[inventoryKey];

      // Get our first timeSlice.
      if (!inventoryValue.series || inventoryValue.series.length === 0) {
        continue;
      }

      const firstSeriesObject = inventoryValue.series[0];

      if (firstSeriesObject.fts) {
        // Use the fts of the first (latest) ts for our frames.  Use only the first animationMaxFrames objects.
        parsedInventory[inventoryKey] = firstSeriesObject.fts
          .map(ftsObject => ({
            ts: firstSeriesObject.ts,
            fts: ftsObject,
          }))
          .sort((a, b) => (a.fts > b.fts ? 1 : -1))
          .slice(0, Math.min(animationMaxFrames, firstSeriesObject.fts.length));
      } else {
        // Use the ts for our frames.  Use only the last animationMaxFrames objects.
        parsedInventory[inventoryKey] = inventoryValue.series
          .map(seriesObject => ({
            ts: seriesObject.ts,
          }))
          .sort((a, b) => (a.ts > b.ts ? 1 : -1))
          .slice(-Math.min(animationMaxFrames, inventoryValue.series.length));
      }
    }
  }

  return (
    <MapUI
      animate={animate}
      attributionStyle={attributionStyle}
      debug={debug}
      homePinSize={homePinSize}
      inventory={parsedInventory}
      inventoryError={inventoryError}
      inventoryIsFetching={inventoryIsFetching}
      isVisibleOverride={isVisibleOverride}
      overlayType={overlayType}
      useDefaultLocation={useDefaultLocation}
      zoomPreset={zoomPreset}
      onFrameChange={onFrameChange}
    />
  );
};

// Provides a top-level convenient map component; mostly just a MapView.  We separate
// MapView from this top-level Map component in order to accommodate differences
// between the native and web Mapbox implementations (while keeping Map.js compact).
const MapUI = ({
  zoomPreset,
  overlayType,
  homePinSize,
  onFrameChange,
  inventory,
  inventoryError,
  inventoryIsFetching,
  attributionStyle,
  animate,
  debug,
  isVisibleOverride,
  useDefaultLocation,
}) => {
  const { t } = useTranslation();

  // Overrides the frame number we are displaying.
  const [debugFrameNumberOverride, setDebugFrameNumberOverride] = useState(null);

  // Location state
  // useDefaultLocation based on alert overlay
  const { coords } = useGetUserLocation(useDefaultLocation);
  const userLocation = [coords.lng, coords.lat];

  // Is the screen currently in view?
  let isVisible = useIsFocused();

  if (isVisibleOverride !== undefined) {
    isVisible = isVisibleOverride;
  }

  // This is the size of the rendered map component.  We use this while calculating map zooms.
  const [mapSize, setMapSize] = useState({ width: null, height: null });

  const computedCenter = ZoomPresetCenter(zoomPreset, userLocation);
  const computedZoom = ZoomPresetZoom(zoomPreset, mapSize.width);

  // This constant takes into account not only the animate prop, but also whether specified overlayType should be animated.  If both are true, then we should try to animate.
  const shouldAnimate = animate && OverlayTypeInfo[overlayType].shouldAnimate;

  // The inventory key to use for the selected overlay type.
  const inventoryKey = OverlayTypeInfo[overlayType].inventoryKey;

  // The inventory for the selected overlay type.
  const selectedOverlayInventory = inventory == null ? null : inventory[inventoryKey];

  // Our priority frame index, or frame closest to now.
  // This is dependent on whether we are displaying observations (last frame) or a forecast (first frame).
  let priorityFrameIndex = 0;

  if (
    selectedOverlayInventory != null &&
    selectedOverlayInventory.length > 0 &&
    selectedOverlayInventory[0].fts == null
  ) {
    priorityFrameIndex = selectedOverlayInventory.length - 1;
  }

  // Keys are frame indices, values are references to the parent view containing the layer tiles.
  const layerReferences = useRef({});

  // The useMapAnimation hook keeps track of the animation counter and the current frame.
  const mapAnimation = useMapAnimation({
    shouldAnimate: shouldAnimate && isVisible,
    priorityFrameIndex,
    holdFrames,
    frameKeys: selectedOverlayInventory,
    onFrameChange: shownFrameIndex => {
      const animatingLayers = Object.values(layerReferences.current).filter(Boolean);

      if (animatingLayers.length === 1) {
        // If we only have a single layer being animated, then show it all the time.
        setLayerOpacity(animatingLayers[0], 1);
      } else {
        // Otherwise show the frame at shownFrameIndex.
        for (const key of Object.keys(layerReferences.current)) {
          // eslint-disable-next-line eqeqeq
          const newOpacity = key == shownFrameIndex ? 1 : 0;
          const frameLayerReference = layerReferences.current[key];

          setLayerOpacity(frameLayerReference, newOpacity);
        }
      }

      updateLegend();
    },
  });

  // We'll show a loading screen when the base map isn't ready yet.
  const [isLoading, setIsLoading] = useState(true);
  const loadingManager = useLoadingManager(newIsLoading => {
    setIsLoading(newIsLoading);
  });

  // The overlay type and zoom preset being shown.
  // When either of these changes, we need to reset mapAnimation.
  const [shownOverlayType, setShownOverlayType] = useState(null);
  const [shownZoomPreset, setShownZoomPreset] = useState(null);

  if (overlayType !== shownOverlayType) {
    setShownOverlayType(overlayType);
  }
  if (zoomPreset !== shownZoomPreset) {
    setShownZoomPreset(zoomPreset);
  }
  useEffect(() => {
    mapAnimation.reset();
    loadingManager.resetExcept([baseMapLoadingKey, symbolLayerLoadingKey]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownOverlayType, shownZoomPreset, mapSize]);

  // Returns the FTS ?? TS value for the specified frame index.
  const timeForFrame = frameIndex => {
    const frameKey = mapAnimation.frameKeyAtIndex(frameIndex);

    if (frameKey != null) {
      return frameKey.fts ?? frameKey.ts;
    }

    return null;
  };

  const showLoadingScreen = () => (inventoryIsFetching && mapAnimation.framesMissing() > 1) || isLoading;

  useEffect(() => {
    updateLegend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, inventoryIsFetching, showLoadingScreen]);

  const updateLegend = () => {
    if (onFrameChange && selectedOverlayInventory != null && selectedOverlayInventory.length > 0) {
      if (!shouldAnimate) {
        const frameNumberShown = selectedOverlayInventory[0].fts ? 0 : selectedOverlayInventory.length - 1;

        onFrameChange(showLoadingScreen(), timeForFrame(frameNumberShown), 1, 1);
      } else if (debugFrameNumberOverride != null) {
        onFrameChange(
          showLoadingScreen(),
          timeForFrame(debugFrameNumberOverride),
          debugFrameNumberOverride,
          mapAnimation.totalFrames,
        );
      } else {
        onFrameChange(
          showLoadingScreen(),
          timeForFrame(mapAnimation.currentFrameIndex()),
          mapAnimation.currentFrameIndex(),
          mapAnimation.totalFrames,
        );
      }
    }
  };

  // Check if we should show the loading screen.
  const loadingScreen = showLoadingScreen() ? <MapLoading key={'mapLoading'} /> : null;

  // Create the base map.
  loadingManager.beginLoadingIfMissing(baseMapLoadingKey);
  const baseMap = (
    <MapTileLayer
      center={computedCenter}
      style={styles.mapLayerVisible}
      tileSizeMethod={MapTileSizeMethod.ceil}
      uriTemplate={apiConfig.MAPBOX_BASE_TILE_TEMPLATE}
      zoom={computedZoom}
      onTilesLoaded={tilesStillLoading => {
        if (tilesStillLoading === 0) {
          loadingManager.finishedLoading(baseMapLoadingKey);
        }
      }}
    />
  );

  // Create the symbol layer.
  loadingManager.beginLoadingIfMissing(symbolLayerLoadingKey);
  const symbolLayer = (
    <MapTileLayer
      center={computedCenter}
      style={styles.mapLayerVisible}
      tileSizeMethod={MapTileSizeMethod.ceil}
      uriTemplate={apiConfig.MAPBOX_SYMBOL_TILE_TEMPLATE}
      zoom={computedZoom}
      onTilesLoaded={tilesStillLoading => {
        if (tilesStillLoading === 0) {
          loadingManager.finishedLoading(symbolLayerLoadingKey);
        }
      }}
    />
  );

  // Create the map overlays.
  const tileOverlays = [];
  // These overlays show on top of symbol layers.
  const promotedOverlays = [];
  const visibleFrameIndex = debugFrameNumberOverride ?? mapAnimation.currentFrameIndex();

  if (isVisible) {
    // Some overlay types display data primarily from another overlay type.  For instance,
    // the Lightning overlay displays a radar animation as it's primary data and overlays
    // lightning on top of that.  We want the parentOverlayType to be used for things like
    // animation, so figure that out here.
    const parentOverlayType = OverlayTypeInfo[overlayType].parentOverlayType ?? overlayType;

    if (shouldAnimate) {
      for (let frameIndex = 0; frameIndex < mapAnimation.totalFrames; frameIndex++) {
        const frameKey = mapAnimation.frameKeyAtIndex(frameIndex);

        const overlayLoadingKey = `overlayFrame-${frameIndex}`;

        if (OverlayTypeInfo[parentOverlayType].waitForLoad) {
          loadingManager.beginLoadingIfMissing(overlayLoadingKey);
        }

        tileOverlays.push(
          <Overlay
            key={frameIndex}
            alpha={frameIndex === visibleFrameIndex ? 1 : 0}
            center={computedCenter}
            forecastTimeSlice={frameKey?.fts}
            frameIndex={frameIndex}
            overlayType={parentOverlayType}
            ref={component => {
              layerReferences.current[frameIndex] = component;
            }}
            timeSlice={frameKey?.ts}
            zoom={computedZoom}
            onOverlayReady={() => {
              if (OverlayTypeInfo[parentOverlayType].waitForLoad) {
                loadingManager.finishedLoading(overlayLoadingKey);
              }
            }}
          />,
        );
      }
    } else {
      const frameKey = mapAnimation.frameKeyAtIndex(visibleFrameIndex);

      const overlayLoadingKey = `overlayFrame-${visibleFrameIndex}`;

      if (OverlayTypeInfo[parentOverlayType].waitForLoad) {
        loadingManager.beginLoadingIfMissing(overlayLoadingKey);
      }

      tileOverlays.push(
        <Overlay
          key={visibleFrameIndex}
          alpha={1}
          center={computedCenter}
          forecastTimeSlice={frameKey?.fts}
          frameIndex={visibleFrameIndex}
          overlayType={parentOverlayType}
          ref={component => {
            layerReferences.current[visibleFrameIndex] = component;
          }}
          timeSlice={frameKey?.ts}
          zoom={computedZoom}
          onOverlayReady={() => {
            if (OverlayTypeInfo[parentOverlayType].waitForLoad) {
              loadingManager.finishedLoading(overlayLoadingKey);
            }
          }}
        />,
      );
    }

    // Add the severe alert overlay if overlayType == .radar
    if (overlayType === OverlayType.radar && zoomPreset === ZoomPreset.local) {
      tileOverlays.push(
        <Overlay
          key={'severe'}
          alpha={1}
          center={computedCenter}
          overlayType={OverlayType.severeAlerts}
          zoom={computedZoom}
        />,
      );
    }

    // The Overlay added for the lightning map above is an animated radar overlay.  Add the static lightning layer here.
    if (overlayType === OverlayType.lightning) {
      tileOverlays.push(
        <Overlay
          key={'lightingOverlay'}
          alpha={1}
          center={computedCenter}
          overlayType={OverlayType.lightning}
          zoom={computedZoom}
        />,
      );
    }

    // Add promoted overlays
    const hasPromotedOverlay = PromotedOverlay.hasPromotedOverlay(parentOverlayType);
    const promotedOverlayLoadingKey = `promotedOverlayFrame-${parentOverlayType}`;

    if (OverlayTypeInfo[parentOverlayType].waitForLoad && hasPromotedOverlay) {
      loadingManager.beginLoadingIfMissing(promotedOverlayLoadingKey);
    }

    if (hasPromotedOverlay) {
      promotedOverlays.push(
        <PromotedOverlay.UI
          key={'promotedOverlay'}
          alpha={1}
          center={computedCenter}
          overlayType={parentOverlayType}
          zoom={computedZoom}
          onOverlayReady={() => {
            if (OverlayTypeInfo[parentOverlayType].waitForLoad) {
              loadingManager.finishedLoading(promotedOverlayLoadingKey);
            }
          }}
        />,
      );
    }
  }

  const mercatorMap = useMercatorMap({
    center: computedCenter,
    zoom: computedZoom,
    tileDimension: 512,
    viewWidth: (mapSize.width ?? 1) * Theme.pixelRatio(),
    viewHeight: (mapSize.height ?? 1) * Theme.pixelRatio(),
  });
  let [homeViewX, homeViewY] = mercatorMap.coordsToViewPoint(...userLocation);

  homeViewX /= Theme.pixelRatio();
  homeViewY /= Theme.pixelRatio();

  const homePin =
    homePinSize && mapSize.width != null && mapSize.height != null ? (
      <MapHomePin size={homePinSize} x={homeViewX} y={homeViewY} />
    ) : null;

  // Create the debug component if needed.
  let debugComponent = null;

  if (debug && isVisible) {
    const frameIndexShown = debugFrameNumberOverride ?? mapAnimation.currentFrameIndex();

    debugComponent = (
      <MapDebugView
        key={'mapDebug'}
        frameNumberOverride={debugFrameNumberOverride}
        frameNumberShown={frameIndexShown}
        fts={mapAnimation.frameKeyAtIndex(frameIndexShown)?.fts}
        maxFrames={mapAnimation.totalFrames}
        setFrameNumberOverride={setDebugFrameNumberOverride}
        ts={mapAnimation.frameKeyAtIndex(frameIndexShown)?.ts}
      />
    );
  }

  // Error display
  if (inventoryError) {
    return (
      <View style={styles.mapErrorWrapper}>
        <Image source={sadCloud} style={styles.mapErrorImage} />
        <TWCText noTestID style={styles.mapErrorText} variant={'mediumText24'}>
          {t(`${errorTPath}noDataText`)}
        </TWCText>
      </View>
    );
  }

  return (
    <View
      style={styles.mapParent}
      onLayout={event => {
        const { width, height } = event.nativeEvent.layout;

        setMapSize({ width, height });
      }}
    >
      {baseMap}
      {tileOverlays}
      {homePin}
      {symbolLayer}
      {promotedOverlays}
      <MapAttribution style={attributionStyle} />
      {debugComponent}
      {loadingScreen}
    </View>
  );
};

export const Map = {
  API: MapAPI,
  UI: MapUI,
};

const styles = StyleSheet.create({
  attributionStyleDefault: {
    bottom: Theme.px(20),
    height: Theme.px(40),
    left: Theme.px(40),
    position: 'absolute',
    right: Theme.px(40),
  },
  mapErrorImage: {
    height: Theme.px(80),
    width: Theme.px(122),
  },
  mapErrorText: {
    paddingLeft: Theme.px(15),
    width: Theme.px(275),
  },
  mapErrorWrapper: {
    alignItems: 'center',
    backgroundColor: Theme.errorBackgroundColor,
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
  },
  mapLayerHidden: {
    bottom: Theme.px(0),
    left: Theme.px(0),
    opacity: 0,
    right: Theme.px(0),
    top: Theme.px(0),
  },
  mapLayerVisible: {
    bottom: Theme.px(0),
    left: Theme.px(0),
    right: Theme.px(0),
    top: Theme.px(0),
  },
  mapParent: {
    bottom: Theme.px(0),
    left: Theme.px(0),
    position: 'absolute',
    right: Theme.px(0),
    top: Theme.px(0),
  },
});

// MapAPI.propTypes = {
//   zoomPreset: PropTypes.oneOf(Object.values(ZoomPreset)).isRequired,
//   overlayType: PropTypes.oneOf(Object.values(OverlayType)).isRequired,
//   homePinSize: PropTypes.oneOf(Object.values(MapHomePinSize)),
//   onFrameChange: PropTypes.func,
//   attributionStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array, () => null]),
//   animate: PropTypes.bool,
//   debug: PropTypes.bool,
//   // This can override the built-in isVisible property, which checks if the screen is focused.  If we are showing a screen that only partially obscures the map (such as the Change Map screen), we can override this to true to make sure overlays are shown on the map.
//   isVisibleOverride: PropTypes.oneOf(PropTypes.bool, PropTypes.undefined),
// };

MapAPI.defaultProps = {
  homePinSize: null,
  onFrameChange: () => {},
  attributionStyle: styles.attributionStyleDefault,
  animate: true,
  debug: false,
  isVisibleOverride: undefined,
};

// MapUI.propTypes = {
//   zoomPreset: PropTypes.oneOf(Object.values(ZoomPreset)).isRequired,
//   overlayType: PropTypes.oneOf(Object.values(OverlayType)).isRequired,
//   homePinSize: PropTypes.oneOf(Object.values(MapHomePinSize)),
//   inventory: PropTypes.object.isRequired,
//   inventoryError: PropTypes.object,
//   inventoryIsFetching: PropTypes.bool.isRequired,
//   onFrameChange: PropTypes.func,
//   attributionStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array, () => null]),
//   animate: PropTypes.bool,
//   debug: PropTypes.bool,
//   // This can override the built-in isVisible property, which checks if the screen is focused.  If we are showing a screen that only partially obscures the map (such as the Change Map screen), we can override this to true to make sure overlays are shown on the map.
//   isVisibleOverride: PropTypes.oneOf(PropTypes.bool, PropTypes.undefined),
// };

MapUI.defaultProps = {
  homePinSize: null,
  inventoryError: null,
  onFrameChange: () => {},
  attributionStyle: styles.attributionStyleDefault,
  animate: true,
  debug: false,
  isVisibleOverride: undefined,
};
